/**
 * BikeComparisonHeader — identity cards (A vs B), delta pill, DTW badge, swap button.
 */
import { Box, Typography, Button, Chip, Alert, useTheme, Tooltip } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDistance(meters) {
  if (meters == null) return '—';
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatSpeed(kph) {
  if (kph == null) return '—';
  return `${kph.toFixed(1)} km/h`;
}

function formatPower(watts) {
  if (watts == null || watts === 0) return '—';
  return `${Math.round(watts)} W`;
}

function DeltaPill({ timeDelta }) {
  if (timeDelta == null) return null;
  const sign = timeDelta > 0 ? '+' : '';
  const abs = Math.abs(timeDelta);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = Math.floor(abs % 60);
  let label;
  if (h > 0) label = `${sign}${h}h ${m.toString().padStart(2, '0')}m`;
  else if (m > 0) label = `${sign}${m}m ${s.toString().padStart(2, '0')}s`;
  else label = `${sign}${s}s`;

  const color = timeDelta === 0 ? '#9ca3af' : timeDelta > 0 ? '#f59e0b' : '#22c55e';
  return (
    <Chip
      label={label}
      size="small"
      sx={{ bgcolor: color + '22', color, border: `1px solid ${color}`, fontWeight: 'bold', fontSize: '0.85rem', px: 1 }}
    />
  );
}

function DtwBadge({ courseMatch }) {
  if (!courseMatch) return null;
  const cfg = {
    good: { color: '#22c55e', label: 'Same course' },
    warn: { color: '#f59e0b', label: 'Similar course' },
    different: { color: '#ef4444', label: 'Different course' },
  }[courseMatch] || { color: '#9ca3af', label: courseMatch };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.color + '22', color: cfg.color, border: `1px solid ${cfg.color}`, fontSize: '0.75rem' }}
    />
  );
}

function WorkoutCard({ analysis, label, color, filename }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      flex={1}
      p={2}
      sx={{
        borderRadius: 2,
        border: `2px solid ${color}`,
        backgroundColor: colors.grey[800],
        minWidth: 0,
      }}
    >
      <Typography variant="caption" sx={{ color, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </Typography>
      {filename && (
        <Tooltip title={filename} placement="bottom-start">
          <Typography variant="caption" color={colors.grey[400]} display="block" noWrap
            sx={{ fontSize: '0.7rem', mt: 0.25, maxWidth: '100%', cursor: 'default' }}>
            {filename}
          </Typography>
        </Tooltip>
      )}
      <Typography variant="body2" color={colors.grey[300]} mt={0.5}>
        {formatDate(analysis?.workout_start_time)}
      </Typography>
      <Box mt={1} display="flex" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>Distance</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            {formatDistance(analysis?.total_distance)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>Duration</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            {formatDuration(analysis?.total_duration)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>Avg Speed</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            {formatSpeed(analysis?.avg_speed)}
          </Typography>
        </Box>
        {analysis?.avg_power > 0 && (
          <Box>
            <Typography variant="caption" color={colors.grey[500]}>Avg Power</Typography>
            <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
              {formatPower(analysis?.avg_power)}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>Elevation</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            +{Math.round(analysis?.total_elevation_gain ?? 0)}m
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function BikeComparisonHeader({
  analysisA,
  analysisB,
  alignment,
  aFilename,
  bFilename,
  onSwap,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const timeDelta = (() => {
    const resampled = alignment?.resampled;
    if (!resampled || resampled.length === 0) return null;
    return resampled[resampled.length - 1]?.time_delta_s ?? null;
  })();

  const courseMatch = alignment?.course_match;

  return (
    <Box>
      {courseMatch === 'different' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These workouts appear to be on different courses. Comparison data may not be meaningful.
        </Alert>
      )}
      <Box display="flex" gap={2} alignItems="stretch" flexWrap="wrap">
        <WorkoutCard analysis={analysisA} label="A" color={COLOR_A} filename={aFilename} />
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
          <DeltaPill timeDelta={timeDelta} />
          <Typography variant="h5" color={colors.grey[500]}>vs</Typography>
          <DtwBadge courseMatch={courseMatch} />
          {onSwap && (
            <Button size="small" variant="outlined" startIcon={<SwapHorizIcon />} onClick={onSwap}
              sx={{ mt: 1, borderColor: colors.grey[600], color: colors.grey[400], '&:hover': { borderColor: colors.grey[400] } }}>
              Swap
            </Button>
          )}
        </Box>
        <WorkoutCard analysis={analysisB} label="B" color={COLOR_B} filename={bFilename} />
      </Box>
    </Box>
  );
}
